import { useRef, useEffect, useCallback } from "react";

interface GoldenGateTideProps {
	currentLevel: number;
	tideStatus: string;
	darkMode?: boolean;
	hoverTime?: number | null;
}

// Linear interpolation between two [r,g,b] colors
function lerpColor(a: number[], b: number[], t: number): number[] {
	return [
		a[0] + (b[0] - a[0]) * t,
		a[1] + (b[1] - a[1]) * t,
		a[2] + (b[2] - a[2]) * t,
	];
}

function rgb(c: number[]): string {
	return `rgb(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])})`;
}

function rgba(c: number[], a: number): string {
	return `rgba(${Math.round(c[0])},${Math.round(c[1])},${Math.round(c[2])},${a})`;
}

// Given fractional hour (0-24), compute a "phase" that blends through:
// night -> dawn -> sunrise -> day -> sunset -> dusk -> night
interface SkyTheme {
	skyTop: number[];
	skyMid: number[];
	skyBottom: number[];
	horizonGlow: number[];
	horizonGlowAlpha: number;
	sunAlpha: number;
	sunY: number; // 0 = horizon, -1 = high in sky, +1 = below horizon
	sunColor: number[];
	sunGlowColor: number[];
	moonAlpha: number;
	moonY: number;
	starAlpha: number;
	hillFar: number[];
	hillNear: number[];
	fogColor: number[];
	fogAlpha: number;
	waterTop: number[];
	waterBottom: number[];
	waveHighlight: number[];
	waveHighlightAlpha: number;
	bridgeColor: number[];
	bridgeHighlight: number[];
	bridgeShadow: number[];
	shoreTop: number[];
	shoreBottom: number[];
	indicatorBg: number[];
	indicatorBorder: number[];
	indicatorText: number[];
	indicatorSubtext: number[];
	indicatorLine: number[];
}

function getTimeOfDayTheme(hour: number): SkyTheme {
	// Define key stops with full color sets
	// hour: 0=midnight, 6=dawn, 7.5=sunrise, 12=noon, 18=sunset, 19.5=dusk, 21=night
	const stops: { h: number; t: SkyTheme }[] = [
		{
			h: 0, t: { // Midnight
				skyTop: [8, 14, 32], skyMid: [12, 20, 40], skyBottom: [18, 28, 52],
				horizonGlow: [18, 28, 52], horizonGlowAlpha: 0,
				sunAlpha: 0, sunY: 1.5, sunColor: [255, 200, 50], sunGlowColor: [255, 150, 50],
				moonAlpha: 0.9, moonY: -0.5,
				starAlpha: 0.8,
				hillFar: [18, 30, 24], hillNear: [22, 38, 30],
				fogColor: [15, 25, 45], fogAlpha: 0.3,
				waterTop: [8, 32, 55], waterBottom: [4, 18, 35],
				waveHighlight: [60, 130, 180], waveHighlightAlpha: 0.06,
				bridgeColor: [100, 22, 22], bridgeHighlight: [130, 28, 28], bridgeShadow: [65, 12, 12],
				shoreTop: [22, 22, 14], shoreBottom: [12, 12, 7],
				indicatorBg: [15, 31, 48], indicatorBorder: [42, 90, 138],
				indicatorText: [126, 200, 232], indicatorSubtext: [90, 154, 181],
				indicatorLine: [100, 200, 255],
			}
		},
		{
			h: 5.5, t: { // Pre-dawn
				skyTop: [12, 18, 45], skyMid: [25, 30, 60], skyBottom: [45, 45, 75],
				horizonGlow: [80, 60, 80], horizonGlowAlpha: 0.2,
				sunAlpha: 0, sunY: 1.2, sunColor: [255, 180, 80], sunGlowColor: [255, 120, 60],
				moonAlpha: 0.4, moonY: -0.7,
				starAlpha: 0.4,
				hillFar: [25, 35, 32], hillNear: [30, 42, 38],
				fogColor: [40, 35, 55], fogAlpha: 0.25,
				waterTop: [15, 38, 65], waterBottom: [8, 22, 42],
				waveHighlight: [80, 140, 190], waveHighlightAlpha: 0.08,
				bridgeColor: [110, 25, 25], bridgeHighlight: [140, 32, 32], bridgeShadow: [72, 15, 15],
				shoreTop: [30, 28, 20], shoreBottom: [18, 16, 10],
				indicatorBg: [20, 35, 55], indicatorBorder: [50, 95, 140],
				indicatorText: [130, 200, 230], indicatorSubtext: [95, 155, 180],
				indicatorLine: [110, 200, 250],
			}
		},
		{
			h: 6.5, t: { // Dawn
				skyTop: [30, 45, 90], skyMid: [80, 70, 100], skyBottom: [180, 120, 100],
				horizonGlow: [220, 150, 80], horizonGlowAlpha: 0.5,
				sunAlpha: 0.4, sunY: 0.3, sunColor: [255, 160, 60], sunGlowColor: [255, 120, 40],
				moonAlpha: 0.1, moonY: -0.9,
				starAlpha: 0.05,
				hillFar: [50, 55, 45], hillNear: [60, 65, 50],
				fogColor: [180, 140, 120], fogAlpha: 0.2,
				waterTop: [40, 60, 90], waterBottom: [20, 35, 55],
				waveHighlight: [200, 160, 120], waveHighlightAlpha: 0.12,
				bridgeColor: [150, 50, 15], bridgeHighlight: [180, 65, 22], bridgeShadow: [100, 32, 8],
				shoreTop: [150, 120, 80], shoreBottom: [120, 95, 60],
				indicatorBg: [40, 45, 55], indicatorBorder: [100, 100, 120],
				indicatorText: [60, 80, 100], indicatorSubtext: [80, 95, 110],
				indicatorLine: [200, 170, 140],
			}
		},
		{
			h: 7.5, t: { // Sunrise
				skyTop: [70, 130, 200], skyMid: [160, 170, 200], skyBottom: [240, 190, 140],
				horizonGlow: [255, 200, 100], horizonGlowAlpha: 0.4,
				sunAlpha: 1, sunY: 0.05, sunColor: [255, 210, 80], sunGlowColor: [255, 180, 60],
				moonAlpha: 0, moonY: -1,
				starAlpha: 0,
				hillFar: [70, 110, 70], hillNear: [85, 125, 80],
				fogColor: [220, 210, 190], fogAlpha: 0.2,
				waterTop: [50, 100, 145], waterBottom: [25, 60, 90],
				waveHighlight: [255, 230, 180], waveHighlightAlpha: 0.15,
				bridgeColor: [175, 58, 12], bridgeHighlight: [210, 80, 25], bridgeShadow: [125, 38, 8],
				shoreTop: [185, 160, 100], shoreBottom: [160, 135, 75],
				indicatorBg: [255, 255, 255], indicatorBorder: [58, 138, 181],
				indicatorText: [26, 95, 122], indicatorSubtext: [74, 143, 165],
				indicatorLine: [255, 255, 255],
			}
		},
		{
			h: 12, t: { // Noon
				skyTop: [135, 206, 235], skyMid: [184, 223, 240], skyBottom: [224, 240, 248],
				horizonGlow: [224, 240, 248], horizonGlowAlpha: 0,
				sunAlpha: 1, sunY: -0.85, sunColor: [255, 250, 200], sunGlowColor: [255, 240, 150],
				moonAlpha: 0, moonY: 1.5,
				starAlpha: 0,
				hillFar: [90, 138, 98], hillNear: [107, 155, 110],
				fogColor: [220, 235, 245], fogAlpha: 0.15,
				waterTop: [45, 127, 168], waterBottom: [26, 95, 122],
				waveHighlight: [255, 255, 255], waveHighlightAlpha: 0.12,
				bridgeColor: [193, 68, 14], bridgeHighlight: [224, 98, 30], bridgeShadow: [138, 46, 8],
				shoreTop: [196, 168, 106], shoreBottom: [168, 144, 80],
				indicatorBg: [255, 255, 255], indicatorBorder: [58, 138, 181],
				indicatorText: [26, 95, 122], indicatorSubtext: [74, 143, 165],
				indicatorLine: [255, 255, 255],
			}
		},
		{
			h: 17, t: { // Late afternoon
				skyTop: [100, 170, 220], skyMid: [170, 190, 210], skyBottom: [230, 210, 180],
				horizonGlow: [240, 200, 130], horizonGlowAlpha: 0.15,
				sunAlpha: 1, sunY: -0.2, sunColor: [255, 220, 100], sunGlowColor: [255, 180, 70],
				moonAlpha: 0, moonY: 1.2,
				starAlpha: 0,
				hillFar: [80, 120, 80], hillNear: [95, 140, 95],
				fogColor: [220, 215, 200], fogAlpha: 0.15,
				waterTop: [40, 110, 155], waterBottom: [22, 80, 110],
				waveHighlight: [255, 240, 200], waveHighlightAlpha: 0.12,
				bridgeColor: [185, 62, 13], bridgeHighlight: [215, 88, 28], bridgeShadow: [130, 42, 8],
				shoreTop: [190, 162, 100], shoreBottom: [162, 138, 76],
				indicatorBg: [255, 255, 255], indicatorBorder: [58, 138, 181],
				indicatorText: [26, 95, 122], indicatorSubtext: [74, 143, 165],
				indicatorLine: [255, 255, 255],
			}
		},
		{
			h: 18.5, t: { // Sunset
				skyTop: [60, 80, 150], skyMid: [150, 100, 110], skyBottom: [240, 140, 70],
				horizonGlow: [255, 130, 40], horizonGlowAlpha: 0.6,
				sunAlpha: 1, sunY: 0.1, sunColor: [255, 140, 40], sunGlowColor: [255, 100, 20],
				moonAlpha: 0.15, moonY: -0.3,
				starAlpha: 0.02,
				hillFar: [55, 50, 50], hillNear: [65, 58, 55],
				fogColor: [180, 120, 80], fogAlpha: 0.2,
				waterTop: [50, 60, 90], waterBottom: [25, 30, 50],
				waveHighlight: [255, 160, 80], waveHighlightAlpha: 0.15,
				bridgeColor: [130, 35, 10], bridgeHighlight: [165, 50, 18], bridgeShadow: [85, 22, 5],
				shoreTop: [130, 100, 60], shoreBottom: [100, 75, 42],
				indicatorBg: [50, 40, 40], indicatorBorder: [130, 90, 60],
				indicatorText: [230, 180, 130], indicatorSubtext: [180, 140, 100],
				indicatorLine: [255, 180, 120],
			}
		},
		{
			h: 19.5, t: { // Dusk
				skyTop: [22, 30, 70], skyMid: [50, 40, 70], skyBottom: [100, 60, 65],
				horizonGlow: [140, 70, 50], horizonGlowAlpha: 0.35,
				sunAlpha: 0, sunY: 0.8, sunColor: [255, 120, 30], sunGlowColor: [200, 80, 20],
				moonAlpha: 0.6, moonY: -0.4,
				starAlpha: 0.35,
				hillFar: [28, 35, 30], hillNear: [35, 42, 36],
				fogColor: [30, 25, 40], fogAlpha: 0.3,
				waterTop: [18, 40, 65], waterBottom: [8, 22, 40],
				waveHighlight: [80, 100, 140], waveHighlightAlpha: 0.08,
				bridgeColor: [108, 24, 24], bridgeHighlight: [135, 30, 30], bridgeShadow: [70, 14, 14],
				shoreTop: [35, 32, 22], shoreBottom: [20, 18, 12],
				indicatorBg: [18, 30, 45], indicatorBorder: [45, 85, 130],
				indicatorText: [120, 195, 225], indicatorSubtext: [88, 150, 175],
				indicatorLine: [100, 180, 240],
			}
		},
		{
			h: 21, t: { // Night
				skyTop: [8, 14, 32], skyMid: [12, 20, 40], skyBottom: [18, 28, 52],
				horizonGlow: [18, 28, 52], horizonGlowAlpha: 0,
				sunAlpha: 0, sunY: 1.5, sunColor: [255, 200, 50], sunGlowColor: [255, 150, 50],
				moonAlpha: 0.9, moonY: -0.6,
				starAlpha: 0.8,
				hillFar: [18, 30, 24], hillNear: [22, 38, 30],
				fogColor: [15, 25, 45], fogAlpha: 0.3,
				waterTop: [8, 32, 55], waterBottom: [4, 18, 35],
				waveHighlight: [60, 130, 180], waveHighlightAlpha: 0.06,
				bridgeColor: [100, 22, 22], bridgeHighlight: [130, 28, 28], bridgeShadow: [65, 12, 12],
				shoreTop: [22, 22, 14], shoreBottom: [12, 12, 7],
				indicatorBg: [15, 31, 48], indicatorBorder: [42, 90, 138],
				indicatorText: [126, 200, 232], indicatorSubtext: [90, 154, 181],
				indicatorLine: [100, 200, 255],
			}
		},
	];

	// Find bounding stops
	// Wrap: treat h=24 same as h=0
	const h = ((hour % 24) + 24) % 24;

	let prev = stops[stops.length - 1];
	let next = stops[0];
	for (let i = 0; i < stops.length; i++) {
		if (h < stops[i].h) {
			next = stops[i];
			prev = i > 0 ? stops[i - 1] : stops[stops.length - 1];
			break;
		}
		if (i === stops.length - 1) {
			prev = stops[i];
			next = stops[0];
		}
	}

	let range = next.h - prev.h;
	if (range <= 0) range += 24;
	let delta = h - prev.h;
	if (delta < 0) delta += 24;
	const t = range > 0 ? delta / range : 0;

	// Smooth interpolation
	const s = t * t * (3 - 2 * t); // smoothstep

	const p = prev.t;
	const n = next.t;
	return {
		skyTop: lerpColor(p.skyTop, n.skyTop, s),
		skyMid: lerpColor(p.skyMid, n.skyMid, s),
		skyBottom: lerpColor(p.skyBottom, n.skyBottom, s),
		horizonGlow: lerpColor(p.horizonGlow, n.horizonGlow, s),
		horizonGlowAlpha: p.horizonGlowAlpha + (n.horizonGlowAlpha - p.horizonGlowAlpha) * s,
		sunAlpha: p.sunAlpha + (n.sunAlpha - p.sunAlpha) * s,
		sunY: p.sunY + (n.sunY - p.sunY) * s,
		sunColor: lerpColor(p.sunColor, n.sunColor, s),
		sunGlowColor: lerpColor(p.sunGlowColor, n.sunGlowColor, s),
		moonAlpha: p.moonAlpha + (n.moonAlpha - p.moonAlpha) * s,
		moonY: p.moonY + (n.moonY - p.moonY) * s,
		starAlpha: p.starAlpha + (n.starAlpha - p.starAlpha) * s,
		hillFar: lerpColor(p.hillFar, n.hillFar, s),
		hillNear: lerpColor(p.hillNear, n.hillNear, s),
		fogColor: lerpColor(p.fogColor, n.fogColor, s),
		fogAlpha: p.fogAlpha + (n.fogAlpha - p.fogAlpha) * s,
		waterTop: lerpColor(p.waterTop, n.waterTop, s),
		waterBottom: lerpColor(p.waterBottom, n.waterBottom, s),
		waveHighlight: lerpColor(p.waveHighlight, n.waveHighlight, s),
		waveHighlightAlpha: p.waveHighlightAlpha + (n.waveHighlightAlpha - p.waveHighlightAlpha) * s,
		bridgeColor: lerpColor(p.bridgeColor, n.bridgeColor, s),
		bridgeHighlight: lerpColor(p.bridgeHighlight, n.bridgeHighlight, s),
		bridgeShadow: lerpColor(p.bridgeShadow, n.bridgeShadow, s),
		shoreTop: lerpColor(p.shoreTop, n.shoreTop, s),
		shoreBottom: lerpColor(p.shoreBottom, n.shoreBottom, s),
		indicatorBg: lerpColor(p.indicatorBg, n.indicatorBg, s),
		indicatorBorder: lerpColor(p.indicatorBorder, n.indicatorBorder, s),
		indicatorText: lerpColor(p.indicatorText, n.indicatorText, s),
		indicatorSubtext: lerpColor(p.indicatorSubtext, n.indicatorSubtext, s),
		indicatorLine: lerpColor(p.indicatorLine, n.indicatorLine, s),
	};
}

export function GoldenGateTide({ currentLevel, tideStatus, darkMode, hoverTime }: GoldenGateTideProps) {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const animRef = useRef<number>(0);
	const levelRef = useRef(currentLevel);
	const statusRef = useRef(tideStatus);
	const darkRef = useRef(darkMode);
	const hoverTimeRef = useRef(hoverTime);
	levelRef.current = currentLevel;
	statusRef.current = tideStatus;
	darkRef.current = darkMode;
	hoverTimeRef.current = hoverTime;

	const draw = useCallback(() => {
		const canvas = canvasRef.current;
		if (!canvas) return;

		const ctx = canvas.getContext("2d");
		if (!ctx) return;

		const dpr = window.devicePixelRatio || 1;
		const rect = canvas.getBoundingClientRect();
		canvas.width = rect.width * dpr;
		canvas.height = rect.height * dpr;
		ctx.scale(dpr, dpr);

		const W = rect.width;
		const H = rect.height;

		function animate(time: number) {
			const curLevel = levelRef.current;
			const curStatus = statusRef.current;
			const curHoverTime = hoverTimeRef.current;
			const isDarkMode = darkRef.current ?? false;

			// Determine time of day
			let hour: number;
			if (curHoverTime != null) {
				const d = new Date(curHoverTime);
				hour = d.getHours() + d.getMinutes() / 60;
			} else {
				// Use current real time when not hovering, but respect dark mode
				const now = new Date();
				hour = now.getHours() + now.getMinutes() / 60;
			}

			const theme = getTimeOfDayTheme(hour);

			// Tide level mapping
			const tideMin = -1;
			const tideMax = 7;
			const tideNorm = Math.max(0, Math.min(1, (curLevel - tideMin) / (tideMax - tideMin)));
			const groundY = H * 0.92;
			const maxWaterY = H * 0.38;
			const waterY = groundY - tideNorm * (groundY - maxWaterY);
			const waveTime = time * 0.001;

			ctx.clearRect(0, 0, W, H);

			// --- SKY ---
			const skyGrad = ctx.createLinearGradient(0, 0, 0, H * 0.55);
			skyGrad.addColorStop(0, rgb(theme.skyTop));
			skyGrad.addColorStop(0.5, rgb(theme.skyMid));
			skyGrad.addColorStop(1, rgb(theme.skyBottom));
			ctx.fillStyle = skyGrad;
			ctx.fillRect(0, 0, W, H);

			// --- HORIZON GLOW ---
			if (theme.horizonGlowAlpha > 0.01) {
				const glowGrad = ctx.createRadialGradient(
					W * 0.5, H * 0.48, 0,
					W * 0.5, H * 0.48, W * 0.6
				);
				glowGrad.addColorStop(0, rgba(theme.horizonGlow, theme.horizonGlowAlpha));
				glowGrad.addColorStop(0.5, rgba(theme.horizonGlow, theme.horizonGlowAlpha * 0.4));
				glowGrad.addColorStop(1, rgba(theme.horizonGlow, 0));
				ctx.fillStyle = glowGrad;
				ctx.fillRect(0, 0, W, H * 0.55);
			}

			// --- STARS ---
			if (theme.starAlpha > 0.01) {
				ctx.save();
				ctx.fillStyle = "#ffffff";
				for (let i = 0; i < 80; i++) {
					const sx = ((i * 137.508 + 42) % W);
					const sy = ((i * 97.31 + 23) % (H * 0.38));
					const sr = 0.4 + (i % 4) * 0.4;
					const twinkle = 0.5 + 0.5 * Math.sin(waveTime * 0.4 + i * 2.1);
					ctx.globalAlpha = theme.starAlpha * twinkle;
					ctx.beginPath();
					ctx.arc(sx, sy, sr, 0, Math.PI * 2);
					ctx.fill();
				}
				ctx.restore();
			}

			// --- SUN ---
			if (theme.sunAlpha > 0.01) {
				// Sun travels an arc: sunY maps to vertical position
				// sunY: -1 = top of sky, 0 = horizon, +1 = below
				const sunCenterX = W * 0.5;
				const horizonLine = H * 0.46;
				const sunRadius = Math.min(W, H) * 0.04;
				const arcHeight = H * 0.4;
				const sunCenterY = horizonLine + theme.sunY * arcHeight;

				if (sunCenterY < horizonLine + sunRadius * 2) {
					// Outer glow
					ctx.save();
					ctx.globalAlpha = theme.sunAlpha * 0.3;
					const glowR = sunRadius * 6;
					const sunGlow = ctx.createRadialGradient(sunCenterX, sunCenterY, sunRadius * 0.5, sunCenterX, sunCenterY, glowR);
					sunGlow.addColorStop(0, rgba(theme.sunGlowColor, 0.4));
					sunGlow.addColorStop(0.4, rgba(theme.sunGlowColor, 0.1));
					sunGlow.addColorStop(1, rgba(theme.sunGlowColor, 0));
					ctx.fillStyle = sunGlow;
					ctx.fillRect(sunCenterX - glowR, sunCenterY - glowR, glowR * 2, glowR * 2);
					ctx.restore();

					// Sun disc
					ctx.save();
					ctx.globalAlpha = theme.sunAlpha;
					const discGrad = ctx.createRadialGradient(sunCenterX, sunCenterY, 0, sunCenterX, sunCenterY, sunRadius);
					discGrad.addColorStop(0, rgb(theme.sunColor));
					discGrad.addColorStop(0.7, rgb(theme.sunColor));
					discGrad.addColorStop(1, rgba(theme.sunGlowColor, 0.6));
					ctx.fillStyle = discGrad;
					ctx.beginPath();
					ctx.arc(sunCenterX, sunCenterY, sunRadius, 0, Math.PI * 2);
					ctx.fill();
					ctx.restore();
				}
			}

			// --- MOON ---
			if (theme.moonAlpha > 0.01) {
				const moonCenterX = W * 0.75;
				const horizonLine = H * 0.46;
				const moonRadius = Math.min(W, H) * 0.028;
				const arcHeight = H * 0.4;
				const moonCenterY = horizonLine + theme.moonY * arcHeight;

				if (moonCenterY < horizonLine + moonRadius * 2) {
					// Moon glow
					ctx.save();
					ctx.globalAlpha = theme.moonAlpha * 0.2;
					const moonGlowR = moonRadius * 5;
					const moonGlow = ctx.createRadialGradient(moonCenterX, moonCenterY, moonRadius, moonCenterX, moonCenterY, moonGlowR);
					moonGlow.addColorStop(0, "rgba(180, 200, 230, 0.3)");
					moonGlow.addColorStop(1, "rgba(180, 200, 230, 0)");
					ctx.fillStyle = moonGlow;
					ctx.fillRect(moonCenterX - moonGlowR, moonCenterY - moonGlowR, moonGlowR * 2, moonGlowR * 2);
					ctx.restore();

					// Moon disc
					ctx.save();
					ctx.globalAlpha = theme.moonAlpha;
					ctx.fillStyle = "#e8eef5";
					ctx.beginPath();
					ctx.arc(moonCenterX, moonCenterY, moonRadius, 0, Math.PI * 2);
					ctx.fill();

					// Crescent shadow
					ctx.globalCompositeOperation = "source-atop";
					ctx.fillStyle = rgb(theme.skyTop);
					ctx.beginPath();
					ctx.arc(moonCenterX + moonRadius * 0.4, moonCenterY - moonRadius * 0.1, moonRadius * 0.85, 0, Math.PI * 2);
					ctx.fill();
					ctx.globalCompositeOperation = "source-over";

					// Moon craters (subtle)
					ctx.globalAlpha = theme.moonAlpha * 0.15;
					ctx.fillStyle = "#c0c8d5";
					ctx.beginPath();
					ctx.arc(moonCenterX - moonRadius * 0.3, moonCenterY + moonRadius * 0.2, moonRadius * 0.12, 0, Math.PI * 2);
					ctx.fill();
					ctx.beginPath();
					ctx.arc(moonCenterX - moonRadius * 0.1, moonCenterY - moonRadius * 0.35, moonRadius * 0.08, 0, Math.PI * 2);
					ctx.fill();
					ctx.restore();
				}
			}

			// --- DISTANT HILLS ---
			ctx.beginPath();
			ctx.moveTo(0, H * 0.45);
			ctx.bezierCurveTo(W * 0.08, H * 0.32, W * 0.18, H * 0.28, W * 0.28, H * 0.35);
			ctx.bezierCurveTo(W * 0.35, H * 0.4, W * 0.38, H * 0.36, W * 0.45, H * 0.38);
			ctx.bezierCurveTo(W * 0.55, H * 0.42, W * 0.6, H * 0.34, W * 0.72, H * 0.37);
			ctx.bezierCurveTo(W * 0.82, H * 0.39, W * 0.9, H * 0.33, W, H * 0.40);
			ctx.lineTo(W, H * 0.55);
			ctx.lineTo(0, H * 0.55);
			ctx.closePath();
			ctx.fillStyle = rgb(theme.hillFar);
			ctx.fill();

			// Closer hills
			ctx.beginPath();
			ctx.moveTo(0, H * 0.5);
			ctx.bezierCurveTo(W * 0.1, H * 0.42, W * 0.2, H * 0.44, W * 0.3, H * 0.48);
			ctx.bezierCurveTo(W * 0.4, H * 0.52, W * 0.55, H * 0.43, W * 0.7, H * 0.47);
			ctx.bezierCurveTo(W * 0.85, H * 0.5, W * 0.95, H * 0.44, W, H * 0.48);
			ctx.lineTo(W, H * 0.55);
			ctx.lineTo(0, H * 0.55);
			ctx.closePath();
			ctx.fillStyle = rgb(theme.hillNear);
			ctx.fill();

			// --- FOG ---
			if (theme.fogAlpha > 0.01) {
				const fogGrad = ctx.createLinearGradient(0, H * 0.38, 0, H * 0.55);
				fogGrad.addColorStop(0, rgba(theme.fogColor, 0));
				fogGrad.addColorStop(0.5, rgba(theme.fogColor, theme.fogAlpha));
				fogGrad.addColorStop(1, rgba(theme.fogColor, 0));
				ctx.fillStyle = fogGrad;
				ctx.fillRect(0, H * 0.38, W, H * 0.17);
			}

			// --- WATER ---
			const waterGrad = ctx.createLinearGradient(0, waterY, 0, groundY);
			waterGrad.addColorStop(0, rgb(theme.waterTop));
			waterGrad.addColorStop(0.3, rgb(lerpColor(theme.waterTop, theme.waterBottom, 0.3)));
			waterGrad.addColorStop(1, rgb(theme.waterBottom));

			ctx.beginPath();
			ctx.moveTo(0, groundY);
			for (let x = 0; x <= W; x += 3) {
				const wave1 = Math.sin(x * 0.015 + waveTime * 0.8) * 3;
				const wave2 = Math.sin(x * 0.025 - waveTime * 1.2) * 2;
				const wave3 = Math.sin(x * 0.008 + waveTime * 0.5) * 4;
				const y = waterY + wave1 + wave2 + wave3;
				ctx.lineTo(x, y);
			}
			ctx.lineTo(W, groundY);
			ctx.closePath();
			ctx.fillStyle = waterGrad;
			ctx.fill();

			// Wave highlights
			ctx.save();
			for (let row = 0; row < 8; row++) {
				const rowY = waterY + row * ((groundY - waterY) / 8);
				const rowAlpha = 1 - row / 8;
				ctx.globalAlpha = theme.waveHighlightAlpha * rowAlpha;
				ctx.beginPath();
				for (let x = 0; x <= W; x += 3) {
					const offset = row * 20;
					const w = Math.sin(x * 0.02 + waveTime * 0.6 + offset) * 2;
					const y = rowY + w + row * 2;
					if (x === 0) ctx.moveTo(x, y);
					else ctx.lineTo(x, y);
				}
				ctx.strokeStyle = rgb(theme.waveHighlight);
				ctx.lineWidth = 1;
				ctx.stroke();
			}
			ctx.restore();

			// Sun/moon reflection on water
			if (theme.sunAlpha > 0.2 && theme.sunY < 0.3) {
				ctx.save();
				const reflectAlpha = theme.sunAlpha * 0.15 * Math.max(0, 1 - theme.sunY * 2);
				for (let row = 0; row < 6; row++) {
					const rowY = waterY + 5 + row * 8;
					ctx.globalAlpha = reflectAlpha * (1 - row / 6);
					ctx.beginPath();
					const cx = W * 0.5;
					const spread = 15 + row * 8;
					for (let x = cx - spread; x <= cx + spread; x += 2) {
						const shimmer = Math.sin(x * 0.3 + waveTime * 2 + row) * 2;
						const y = rowY + shimmer;
						if (x === cx - spread) ctx.moveTo(x, y);
						else ctx.lineTo(x, y);
					}
					ctx.strokeStyle = rgba(theme.sunColor, 0.6);
					ctx.lineWidth = 2;
					ctx.stroke();
				}
				ctx.restore();
			}

			if (theme.moonAlpha > 0.3 && theme.moonY < 0) {
				ctx.save();
				const reflectAlpha = theme.moonAlpha * 0.08;
				for (let row = 0; row < 4; row++) {
					const rowY = waterY + 5 + row * 6;
					ctx.globalAlpha = reflectAlpha * (1 - row / 4);
					ctx.beginPath();
					const cx = W * 0.75;
					const spread = 8 + row * 5;
					for (let x = cx - spread; x <= cx + spread; x += 2) {
						const shimmer = Math.sin(x * 0.4 + waveTime * 1.5 + row) * 1.5;
						const y = rowY + shimmer;
						if (x === cx - spread) ctx.moveTo(x, y);
						else ctx.lineTo(x, y);
					}
					ctx.strokeStyle = "rgba(200, 215, 240, 0.4)";
					ctx.lineWidth = 1.5;
					ctx.stroke();
				}
				ctx.restore();
			}

			// --- BRIDGE ---
			const bridgeColor = rgb(theme.bridgeColor);
			const bridgeHighlight = rgb(theme.bridgeHighlight);
			const bridgeShadow = rgb(theme.bridgeShadow);
			const cableColor = rgb(lerpColor(theme.bridgeShadow, theme.bridgeColor, 0.5));

			const towerWidth = W * 0.035;
			const tower1X = W * 0.28;
			const tower2X = W * 0.72;
			const towerTop = H * 0.08;
			const deckY = H * 0.44;
			const deckThickness = H * 0.025;

			function drawTower(cx: number) {
				const left = cx - towerWidth / 2;
				const right = cx + towerWidth / 2;

				ctx.fillStyle = bridgeColor;
				ctx.fillRect(left, towerTop, towerWidth, deckY - towerTop + deckThickness);

				const legWidth = towerWidth * 0.4;
				ctx.fillStyle = bridgeColor;
				ctx.fillRect(left, deckY + deckThickness, legWidth, groundY - deckY);
				ctx.fillRect(right - legWidth, deckY + deckThickness, legWidth, groundY - deckY);

				ctx.fillStyle = bridgeHighlight;
				ctx.fillRect(left, towerTop, towerWidth * 0.2, deckY - towerTop + deckThickness);

				ctx.fillStyle = bridgeShadow;
				ctx.fillRect(right - towerWidth * 0.15, towerTop, towerWidth * 0.15, deckY - towerTop + deckThickness);

				const braceCount = 5;
				for (let i = 1; i <= braceCount; i++) {
					const braceY = towerTop + (i / (braceCount + 1)) * (deckY - towerTop);
					ctx.fillStyle = bridgeShadow;
					ctx.fillRect(left - 1, braceY - 2, towerWidth + 2, 4);
					ctx.fillStyle = bridgeHighlight;
					ctx.fillRect(left, braceY - 1, towerWidth, 2);
				}

				ctx.fillStyle = bridgeHighlight;
				ctx.fillRect(left - 2, towerTop - 3, towerWidth + 4, 6);
				ctx.fillStyle = bridgeShadow;
				ctx.fillRect(left - 2, towerTop - 4, towerWidth + 4, 2);
			}

			function catenary(x: number, x1: number, x2: number, topY: number, sagY: number) {
				const t = (x - x1) / (x2 - x1);
				return topY + sagY * (4 * t * t - 4 * t + 1 - 1) * -1;
			}

			// Suspender cables
			ctx.strokeStyle = cableColor;
			ctx.lineWidth = 1;
			const suspenderSpacing = W * 0.03;
			const isDarkish = theme.starAlpha > 0.2;

			for (let x = tower1X + suspenderSpacing; x < tower2X; x += suspenderSpacing) {
				const mainCableY = catenary(x, tower1X, tower2X, towerTop + 2, (deckY - towerTop) * 0.85);
				ctx.globalAlpha = isDarkish ? 0.4 : 0.5;
				ctx.beginPath();
				ctx.moveTo(x, mainCableY);
				ctx.lineTo(x, deckY);
				ctx.stroke();
			}
			for (let x = 0; x < tower1X; x += suspenderSpacing) {
				const cableY = catenary(x, -W * 0.1, tower1X, towerTop + 2, (deckY - towerTop) * 0.5);
				if (cableY < deckY) { ctx.beginPath(); ctx.moveTo(x, cableY); ctx.lineTo(x, deckY); ctx.stroke(); }
			}
			for (let x = tower2X + suspenderSpacing; x < W; x += suspenderSpacing) {
				const cableY = catenary(x, tower2X, W * 1.1, towerTop + 2, (deckY - towerTop) * 0.5);
				if (cableY < deckY) { ctx.beginPath(); ctx.moveTo(x, cableY); ctx.lineTo(x, deckY); ctx.stroke(); }
			}
			ctx.globalAlpha = 1;

			// Deck
			ctx.fillStyle = bridgeShadow;
			ctx.fillRect(0, deckY + 2, W, deckThickness);
			const deckGrad = ctx.createLinearGradient(0, deckY, 0, deckY + deckThickness);
			deckGrad.addColorStop(0, bridgeHighlight);
			deckGrad.addColorStop(0.5, bridgeColor);
			deckGrad.addColorStop(1, bridgeShadow);
			ctx.fillStyle = deckGrad;
			ctx.fillRect(0, deckY, W, deckThickness);
			ctx.fillStyle = bridgeHighlight;
			ctx.fillRect(0, deckY - 2, W, 2);

			// --- TRAFFIC ---
			// Traffic density model:
			// Left->Right = morning commute (home->work), peaks ~8-10am
			// Right->Left = evening commute (work->home), peaks ~5-7pm
			// Light traffic at night (2am-5am), moderate otherwise
			{
				// Traffic density for each direction (0-1)
				function trafficDensity(h: number, direction: "toWork" | "toHome"): number {
					// Base: light overnight, moderate during day
					// Gaussian-ish peaks for rush hours
					const gaussian = (x: number, mu: number, sigma: number) =>
						Math.exp(-0.5 * ((x - mu) / sigma) ** 2);

					// Overnight baseline (very low 1am-5am)
					const nightDip = 1 - 0.7 * gaussian(h, 3, 1.5);
					let base = 0.15 * nightDip;

					if (direction === "toWork") {
						// Morning rush: peak at 8:30am, shoulder 7-10am
						base += 0.85 * gaussian(h, 8.5, 1.2);
						// Small midday
						base += 0.15 * gaussian(h, 12, 2);
					} else {
						// Evening rush: peak at 5:30pm, shoulder 4-7pm
						base += 0.85 * gaussian(h, 17.5, 1.2);
						// Small midday
						base += 0.15 * gaussian(h, 12, 2);
					}
					return Math.min(1, Math.max(0.02, base));
				}

				const toWorkDensity = trafficDensity(hour, "toWork");
				const toHomeDensity = trafficDensity(hour, "toHome");

				// Car dimensions
				const carW = W * 0.018;
				const carH = deckThickness * 0.35;
				const laneOffset = deckThickness * 0.18;
				// Two lanes per direction: top half = toWork (left->right), bottom half = toHome (right->left)
				const lane1Y = deckY + laneOffset; // toWork lane 1
				const lane2Y = deckY + laneOffset + carH + 1; // toWork lane 2
				const lane3Y = deckY + deckThickness - laneOffset - carH * 2 - 1; // toHome lane 1
				const lane4Y = deckY + deckThickness - laneOffset - carH; // toHome lane 2

				// Speed: slower when more dense (congestion)
				const toWorkSpeed = 0.3 + (1 - toWorkDensity) * 0.7; // 0.3-1.0 multiplier
				const toHomeSpeed = 0.3 + (1 - toHomeDensity) * 0.7;

				// Car spacing depends on density: more dense = tighter spacing
				const maxCars = 20;
				const toWorkCount = Math.max(1, Math.round(toWorkDensity * maxCars));
				const toHomeCount = Math.max(1, Math.round(toHomeDensity * maxCars));

				// Road extent
				const roadLeft = W * 0.02;
				const roadRight = W * 0.98;
				const roadLen = roadRight - roadLeft;

				// Deterministic pseudo-random for car colors
				function carSeed(i: number, lane: number): number {
					return ((i * 137.508 + lane * 97.31) % 1000) / 1000;
				}

				const isNight = theme.starAlpha > 0.15;

				// Car colors (varied)
				const carColors = [
					[220, 220, 225], // silver
					[40, 40, 45],    // black
					[200, 200, 200], // white
					[60, 60, 65],    // dark gray
					[140, 25, 25],   // dark red
					[25, 45, 100],   // dark blue
					[35, 80, 45],    // dark green
					[170, 140, 80],  // tan/gold
				];

				function drawCar(x: number, y: number, goingRight: boolean, seed: number) {
					const colorIdx = Math.floor(seed * carColors.length);
					const c = carColors[colorIdx];
					const brightness = isNight ? 0.4 : 1;
					const cr = Math.round(c[0] * brightness);
					const cg = Math.round(c[1] * brightness);
					const cb = Math.round(c[2] * brightness);

					// Car body
					ctx.fillStyle = `rgb(${cr},${cg},${cb})`;
					ctx.fillRect(x, y, carW, carH);

					// Windshield
					const wsX = goingRight ? x + carW * 0.65 : x + carW * 0.05;
					ctx.fillStyle = isNight ? "rgba(80,100,130,0.7)" : "rgba(150,190,220,0.8)";
					ctx.fillRect(wsX, y, carW * 0.3, carH);

					// Headlights at night
					if (isNight) {
						ctx.save();
						const hlX = goingRight ? x + carW : x;
						const hlY = y + carH / 2;

						// Headlight beam
						ctx.globalAlpha = 0.15;
						const beamLen = carW * 4;
						const beamGrad = goingRight
							? ctx.createLinearGradient(hlX, hlY, hlX + beamLen, hlY)
							: ctx.createLinearGradient(hlX, hlY, hlX - beamLen, hlY);
						beamGrad.addColorStop(0, "rgba(255,240,200,0.6)");
						beamGrad.addColorStop(1, "rgba(255,240,200,0)");
						ctx.fillStyle = beamGrad;
						if (goingRight) {
							ctx.fillRect(hlX, y - 1, beamLen, carH + 2);
						} else {
							ctx.fillRect(hlX - beamLen, y - 1, beamLen, carH + 2);
						}

						// Headlight dot
						ctx.globalAlpha = 0.9;
						ctx.fillStyle = "#fff8e0";
						ctx.beginPath();
						ctx.arc(hlX, hlY, 1.2, 0, Math.PI * 2);
						ctx.fill();

						// Tail lights (red, on opposite end)
						ctx.globalAlpha = 0.8;
						ctx.fillStyle = "#ff3030";
						const tlX = goingRight ? x : x + carW;
						ctx.beginPath();
						ctx.arc(tlX, y + 1, 0.8, 0, Math.PI * 2);
						ctx.fill();
						ctx.beginPath();
						ctx.arc(tlX, y + carH - 1, 0.8, 0, Math.PI * 2);
						ctx.fill();

						ctx.restore();
					}
				}

				ctx.save();
				// Clip to deck area so cars don't overflow
				ctx.beginPath();
				ctx.rect(roadLeft, deckY, roadLen, deckThickness);
				ctx.clip();

				// Draw toWork cars (left -> right) on lanes 1 & 2
				const toWorkBaseSpeed = waveTime * 30 * toWorkSpeed;
				for (let i = 0; i < toWorkCount; i++) {
					const s = carSeed(i, 0);
					const spacing = roadLen / toWorkCount;
					const lane = i % 2 === 0 ? lane1Y : lane2Y;
					const stagger = (i % 2) * spacing * 0.4; // offset between lanes
					let x = roadLeft + ((toWorkBaseSpeed + i * spacing + stagger) % roadLen);
					// Slight speed variation per car
					const speedJitter = (s - 0.5) * spacing * 0.2;
					x = roadLeft + ((x - roadLeft + speedJitter + roadLen) % roadLen);
					drawCar(x, lane, true, s);
				}

				// Draw toHome cars (right -> left) on lanes 3 & 4
				const toHomeBaseSpeed = waveTime * 30 * toHomeSpeed;
				for (let i = 0; i < toHomeCount; i++) {
					const s = carSeed(i, 2);
					const spacing = roadLen / toHomeCount;
					const lane = i % 2 === 0 ? lane3Y : lane4Y;
					const stagger = (i % 2) * spacing * 0.4;
					let x = roadRight - ((toHomeBaseSpeed + i * spacing + stagger) % roadLen);
					const speedJitter = (s - 0.5) * spacing * 0.2;
					x = roadLeft + ((x - roadLeft + speedJitter + roadLen * 10) % roadLen);
					drawCar(x, lane, false, s);
				}

				ctx.restore();
			}

			// Main cables
			ctx.lineWidth = 3.5;
			ctx.lineCap = "round";
			ctx.strokeStyle = bridgeColor;
			ctx.beginPath();
			for (let x = tower1X; x <= tower2X; x += 2) {
				const y = catenary(x, tower1X, tower2X, towerTop + 2, (deckY - towerTop) * 0.85);
				if (x === tower1X) ctx.moveTo(x, y); else ctx.lineTo(x, y);
			}
			ctx.stroke();
			ctx.strokeStyle = bridgeHighlight;
			ctx.lineWidth = 1.5;
			ctx.beginPath();
			for (let x = tower1X; x <= tower2X; x += 2) {
				const y = catenary(x, tower1X, tower2X, towerTop + 2, (deckY - towerTop) * 0.85) - 1;
				if (x === tower1X) ctx.moveTo(x, y); else ctx.lineTo(x, y);
			}
			ctx.stroke();
			ctx.strokeStyle = bridgeColor;
			ctx.lineWidth = 3.5;
			ctx.beginPath();
			for (let x = 0; x <= tower1X; x += 2) {
				const y = catenary(x, -W * 0.1, tower1X, towerTop + 2, (deckY - towerTop) * 0.5);
				if (x === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
			}
			ctx.stroke();
			ctx.beginPath();
			for (let x = tower2X; x <= W; x += 2) {
				const y = catenary(x, tower2X, W * 1.1, towerTop + 2, (deckY - towerTop) * 0.5);
				if (x === tower2X) ctx.moveTo(x, y); else ctx.lineTo(x, y);
			}
			ctx.stroke();

			drawTower(tower1X);
			drawTower(tower2X);

			// Water overlay on tower legs
			if (waterY < groundY) {
				ctx.save();
				ctx.globalAlpha = 0.7;
				const waterOverlayGrad = ctx.createLinearGradient(0, waterY, 0, groundY);
				waterOverlayGrad.addColorStop(0, rgb(theme.waterTop));
				waterOverlayGrad.addColorStop(1, rgb(theme.waterBottom));
				ctx.beginPath();
				ctx.moveTo(0, groundY);
				for (let x = 0; x <= W; x += 3) {
					const wave1 = Math.sin(x * 0.015 + waveTime * 0.8) * 3;
					const wave2 = Math.sin(x * 0.025 - waveTime * 1.2) * 2;
					const wave3 = Math.sin(x * 0.008 + waveTime * 0.5) * 4;
					const y = waterY + wave1 + wave2 + wave3;
					ctx.lineTo(x, y);
				}
				ctx.lineTo(W, groundY);
				ctx.closePath();
				ctx.fillStyle = waterOverlayGrad;
				ctx.fill();
				ctx.restore();
			}

			// --- SHORE ---
			const shoreGrad = ctx.createLinearGradient(0, groundY - 5, 0, H);
			shoreGrad.addColorStop(0, rgb(theme.shoreTop));
			shoreGrad.addColorStop(1, rgb(theme.shoreBottom));
			ctx.fillStyle = shoreGrad;
			ctx.beginPath();
			ctx.moveTo(0, groundY);
			ctx.lineTo(0, H);
			ctx.lineTo(W, H);
			ctx.lineTo(W, groundY);
			for (let x = W; x >= 0; x -= 3) {
				const y = groundY + Math.sin(x * 0.01) * 2;
				ctx.lineTo(x, y);
			}
			ctx.closePath();
			ctx.fill();

			// --- TIDE LEVEL INDICATOR ---
			ctx.save();
			const indicatorX = W - 12;

			ctx.strokeStyle = rgba(theme.indicatorLine, 0.6);
			ctx.lineWidth = 1;
			ctx.setLineDash([3, 3]);
			ctx.beginPath();
			ctx.moveTo(W * 0.85, waterY);
			ctx.lineTo(indicatorX, waterY);
			ctx.stroke();
			ctx.setLineDash([]);

			const levelText = `${curLevel.toFixed(1)} ft`;
			const statusText = curStatus;
			ctx.font = "bold 13px system-ui, sans-serif";
			const tw = Math.max(ctx.measureText(levelText).width, ctx.measureText(statusText).width);
			const boxW = tw + 14;
			const boxH = 38;
			const boxX = W - boxW - 8;
			const boxY = waterY - boxH / 2;

			ctx.globalAlpha = 0.85;
			ctx.fillStyle = rgba(theme.indicatorBg, 0.92);
			ctx.beginPath();
			const rr = 5;
			ctx.moveTo(boxX + rr, boxY);
			ctx.lineTo(boxX + boxW - rr, boxY);
			ctx.arcTo(boxX + boxW, boxY, boxX + boxW, boxY + rr, rr);
			ctx.lineTo(boxX + boxW, boxY + boxH - rr);
			ctx.arcTo(boxX + boxW, boxY + boxH, boxX + boxW - rr, boxY + boxH, rr);
			ctx.lineTo(boxX + rr, boxY + boxH);
			ctx.arcTo(boxX, boxY + boxH, boxX, boxY + boxH - rr, rr);
			ctx.lineTo(boxX, boxY + rr);
			ctx.arcTo(boxX, boxY, boxX + rr, boxY, rr);
			ctx.closePath();
			ctx.fill();
			ctx.globalAlpha = 1;
			ctx.strokeStyle = rgb(theme.indicatorBorder);
			ctx.lineWidth = 1;
			ctx.stroke();

			ctx.textAlign = "center";
			ctx.textBaseline = "middle";
			ctx.fillStyle = rgb(theme.indicatorText);
			ctx.font = "bold 13px system-ui, sans-serif";
			ctx.fillText(levelText, boxX + boxW / 2, boxY + 12);
			ctx.fillStyle = rgb(theme.indicatorSubtext);
			ctx.font = "11px system-ui, sans-serif";
			ctx.fillText(statusText, boxX + boxW / 2, boxY + 28);

			ctx.restore();

			animRef.current = requestAnimationFrame(animate);
		}

		animRef.current = requestAnimationFrame(animate);
	}, []);

	useEffect(() => {
		draw();
		return () => {
			if (animRef.current) cancelAnimationFrame(animRef.current);
		};
	}, [draw]);

	useEffect(() => {
		const handleResize = () => {
			if (animRef.current) cancelAnimationFrame(animRef.current);
			draw();
		};
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, [draw]);

	return (
		<canvas
			ref={canvasRef}
			className="w-full h-full rounded-lg"
			style={{ width: "100%", height: "100%" }}
		/>
	);
}
